import { Copy } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { authAxios } from "../utils/auth";

// ─── CC marker tolerance ─────────────────────────────────────────────
// Single source of truth for the [CC>...<CC] copy-block markers. LLMs
// (DeepSeek especially) sometimes drift on the punctuation — emitting
// `<CC>` instead of `<CC]`, or `[CC]` instead of `[CC>` — which used to
// drop the entire block to raw text and leak the markers into the
// rendered message. We now accept any bracket/angle combo on either
// side, plus XML/HTML-style closers (`[/CC]`, `</CC>`).
//
//   OPEN   any of:  [CC>   [CC]   <CC>   <CC]
//   CLOSE  any of:  the four openers above + [/CC]  </CC>  [/CC>  </CC]
const CC_OPEN_G = /[[<]CC[>\]]/g;
const CC_CLOSE_G = /[[<]\/?CC[>\]]/g;
// Non-greedy pair — first valid closer wins. Used for standalone blocks.
const CC_PAIR = /[[<]CC[>\]]([\s\S]*?)[[<]\/?CC[>\]]/g;
// Inline pair also accepts the escaped markers (⟦CC⟩ … ⟨CC⟧) that the
// standalone path emits when it hands a CC-bearing segment off to
// ReactMarkdown — without them, nested inline blocks would render raw.
const CC_PAIR_INLINE =
  /(?:[[<]CC[>\]]|⟦CC⟩)([\s\S]*?)(?:[[<]\/?CC[>\]]|⟨CC⟧)/g;

// Rewrites any loose CC marker (open OR close) into the escaped form
// the inline path expects. Order matters: replace openers first so the
// closer pass only catches the XML-style `/`-variants that survived.
const escapeCCMarkers = (s) =>
  s
    .replace(/[[<]CC[>\]]/g, "⟦CC⟩")
    .replace(/[[<]\/?CC[>\]]/g, "⟨CC⟧");

// Helper to render inline CC blocks within text
const renderWithInlineCopyBlocks = (children) => {
  // If children is not a string, just return it
  if (typeof children !== 'string') {
    // If it's an array, process each child
    if (Array.isArray(children)) {
      return children.map((child, idx) => {
        if (typeof child === 'string') {
          return <InlineCopyRenderer key={idx} text={child} />;
        }
        return child;
      });
    }
    return children;
  }
  
  return <InlineCopyRenderer text={children} />;
};

// Component to render text with inline CC copy blocks. Accepts every
// loose marker variant (see CC_PAIR_INLINE) so a malformed closer like
// `<CC>` doesn't leak the raw marker into the bubble.
const InlineCopyRenderer = ({ text }) => {
  const ccPattern = new RegExp(CC_PAIR_INLINE.source, "g");
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = ccPattern.exec(text)) !== null) {
    // Add text before the CC block
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add the inline copyable content with button
    const copyableContent = match[1].trim();
    if (copyableContent) {
      parts.push(
        <span key={keyIndex++} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-divine-gold/10 border border-divine-gold/30">
          <span>{copyableContent}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(copyableContent);
              toast.success("Copied!");
            }}
            className="p-0.5 rounded hover:bg-divine-gold/30 transition-colors"
            title="Copy"
          >
            <Copy className="w-3 h-3 text-divine-gold" />
          </button>
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no CC blocks found, return original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
};

// Theme-aware markdown components using CSS variables (auto-switch via data-theme)
const markdownComponents = {
  // Headings
  h1: ({ children }) => (
    <h1 className="text-xl font-bold mt-4 mb-2 text-divine-gold">
      {renderWithInlineCopyBlocks(children)}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-semibold mt-3 mb-2 text-divine-gold">
      {renderWithInlineCopyBlocks(children)}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-semibold mt-3 mb-1 text-divine-gold-light">
      {renderWithInlineCopyBlocks(children)}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-medium mt-2 mb-1 text-divine-gold-light">
      {renderWithInlineCopyBlocks(children)}
    </h4>
  ),
  
  // Paragraphs
  p: ({ children }) => (
    <p className="mb-2 last:mb-0 leading-relaxed">
      {renderWithInlineCopyBlocks(children)}
    </p>
  ),
  
  // Bold & Italic
  strong: ({ children }) => (
    <strong className="font-semibold text-divine-gold">
      {renderWithInlineCopyBlocks(children)}
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic opacity-90">{renderWithInlineCopyBlocks(children)}</em>
  ),
  
  // Lists
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-2 space-y-1 ml-2">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside mb-2 space-y-1 ml-2">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{renderWithInlineCopyBlocks(children)}</li>
  ),
  
  // Code
  code: ({ inline, children }) => (
    inline ? (
      <code className="px-1.5 py-0.5 rounded text-sm bg-divine-gold/20 text-divine-gold">
        {children}
      </code>
    ) : (
      <code className="block p-3 rounded-lg my-2 text-sm overflow-x-auto bg-divine-gold/10 border border-divine-gold/20">
        {children}
      </code>
    )
  ),
  pre: ({ children }) => (
    <pre className="my-2">{children}</pre>
  ),
  
  // Blockquote
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 pl-3 my-2 italic opacity-80 border-divine-gold/50">
      {children}
    </blockquote>
  ),
  
  // Horizontal rule
  hr: () => (
    <hr className="my-3 border-t border-divine-gold/30" />
  ),
  
  // Links
  a: ({ href, children }) => (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="underline text-divine-gold hover:text-divine-gold-light"
    >
      {renderWithInlineCopyBlocks(children)}
    </a>
  ),
  
  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-divine-gold/20 border-b-2 border-divine-gold/40">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-divine-gold/20">
      {children}
    </tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-divine-gold/5">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-divine-gold">
      {renderWithInlineCopyBlocks(children)}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2">
      {renderWithInlineCopyBlocks(children)}
    </td>
  ),
};

// Render markdown content with theme support via CSS variables
const MarkdownRenderer = ({ content }) => {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
};

// Check if content has standalone CC blocks (outside of tables/inline contexts)
const hasStandaloneCCBlocks = (content) => {
  // Check if there are CC blocks that appear to be standalone (on their own lines).
  // Use the same loose opener/closer tolerance the parser uses.
  const openLineRe = /^[[<]CC[>\]]/;
  const closeLineRe = /[[<]\/?CC[>\]]$/;
  const lines = content.split("\n");
  for (const line of lines) {
    const trimmed = line.trim();
    if (openLineRe.test(trimmed) && closeLineRe.test(trimmed)) {
      return true;
    }
  }
  return false;
};

// Helper component to render messages with [CC> <CC] copy blocks and markdown.
// When `matchPersonaId` and `sessionId` are provided, clicking the Copy
// button on a CC block ALSO PATCHes /api/outbound-copy/mark-sent so the
// pre-session briefing can correctly differentiate (sent) vs (pending)
// suggestions. Optional props — TestChat and other non-auth surfaces
// can omit them and the copy still works locally.
const MessageContent = ({ content, matchPersonaId, sessionId }) => {
  // Safety check for null/undefined content
  if (!content) return null;

  // Gate: if there's any loose-form opener anywhere in the content, run
  // the CC parser. We deliberately do NOT require a matching closer here
  // — the parser has an unclosed-opener fallback that wraps opener→EOM
  // when the LLM forgot/garbled the closer.
  const hasCCBlocks = new RegExp(CC_OPEN_G.source).test(content);

  // If no CC blocks, just render through markdown (handles tables automatically)
  if (!hasCCBlocks) {
    return (
      <div className="prose-content leading-relaxed">
        <MarkdownRenderer content={content} />
      </div>
    );
  }

  // ALWAYS process CC blocks FIRST, regardless of whether there are tables.
  // The pair regex is non-greedy + tolerant of every opener/closer variant
  // an LLM might emit (see CC_PAIR comment block).
  const ccPattern = new RegExp(CC_PAIR.source, "g");
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = ccPattern.exec(content)) !== null) {
    // Add text before the CC block (may contain tables or other markdown)
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push(
          <div key={keyIndex++} className="prose-content">
            <MarkdownRenderer content={escapeCCMarkers(textBefore)} />
          </div>
        );
      }
    }

    // Add the copyable block (standalone style)
    const copyableContent = match[1].trim();
    if (copyableContent) {
      parts.push(
        <div key={keyIndex++} className="relative group my-2">
          <div className="rounded-lg p-3 pr-10 bg-divine-gold/10 border border-divine-gold/30">
            <div className="prose-content whitespace-pre-wrap font-mono text-sm">
              {copyableContent}
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(copyableContent);
              } catch {
                // copy failed — toast it as an error rather than success
                toast.error("Copy failed");
                return;
              }
              toast.success("Copied!");
              // Fire-and-forget mark-sent telemetry so the pre-session
              // briefing knows this CC block was actually delivered.
              if (matchPersonaId && sessionId) {
                try {
                  await authAxios.post("/outbound-copy/mark-sent", {
                    match_persona_id: matchPersonaId,
                    session_id: sessionId,
                    message_text: copyableContent,
                  });
                } catch {
                  // Server-side failure is non-blocking — user already
                  // got the copy + success toast. Don't disrupt the flow.
                }
              }
            }}
            className="absolute right-2 top-2 p-1.5 rounded transition-all opacity-60 group-hover:opacity-100 bg-divine-gold/20 hover:bg-divine-gold/40"
            title="Copy"
            data-testid="cc-copy-btn"
          >
            <Copy className="w-3 h-3 text-divine-gold" />
          </button>
        </div>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // ─── Unclosed-opener fallback ────────────────────────────────────────
  // The LLM may have emitted an opener with no (recognisable) closer
  // before end-of-message — e.g. ran out of max_tokens mid-block, or
  // emitted a closer shape we still don't recognise. Rather than leak
  // the raw marker into the bubble, treat from the opener to EOM as a
  // CC block. The text segment BEFORE the opener still flows through
  // markdown.
  const tail = content.slice(lastIndex);
  if (tail.length > 0) {
    const openerRe = new RegExp(CC_OPEN_G.source);
    const openerMatch = openerRe.exec(tail);
    if (openerMatch) {
      const textBeforeOpener = tail.slice(0, openerMatch.index);
      if (textBeforeOpener.trim()) {
        parts.push(
          <div key={keyIndex++} className="prose-content">
            <MarkdownRenderer content={escapeCCMarkers(textBeforeOpener)} />
          </div>
        );
      }
      const copyableContent = tail
        .slice(openerMatch.index + openerMatch[0].length)
        .trim();
      if (copyableContent) {
        parts.push(
          <div key={keyIndex++} className="relative group my-2">
            <div className="rounded-lg p-3 pr-10 bg-divine-gold/10 border border-divine-gold/30">
              <div className="prose-content whitespace-pre-wrap font-mono text-sm">
                {copyableContent}
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(copyableContent);
                } catch {
                  toast.error("Copy failed");
                  return;
                }
                toast.success("Copied!");
                if (matchPersonaId && sessionId) {
                  try {
                    await authAxios.post("/outbound-copy/mark-sent", {
                      match_persona_id: matchPersonaId,
                      session_id: sessionId,
                      message_text: copyableContent,
                    });
                  } catch {
                    // non-blocking
                  }
                }
              }}
              className="absolute right-2 top-2 p-1.5 rounded transition-all opacity-60 group-hover:opacity-100 bg-divine-gold/20 hover:bg-divine-gold/40"
              title="Copy"
              data-testid="cc-copy-btn"
            >
              <Copy className="w-3 h-3 text-divine-gold" />
            </button>
          </div>
        );
      }
    } else if (tail.trim()) {
      // No further opener — render whatever's left as markdown.
      parts.push(
        <div key={keyIndex++} className="prose-content">
          <MarkdownRenderer content={escapeCCMarkers(tail)} />
        </div>
      );
    }
  }

  return <div className="leading-relaxed">{parts}</div>;
};

// Export helpers for use in other components
export { MarkdownRenderer, markdownComponents };
export default MessageContent;
